/**
 * screens/products/catalog.tsx — Catalog product surface (UI shell, mock data).
 * Faithful RN port of design/products.jsx CatalogPage + CatalogEditor: stat
 * strip, search + status filter chips, items table with stock coloring, and a
 * product/service editor drawer (type toggles the stock field).
 */

import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { ProductHeader, StatStrip, PButton, PTextInput, PSelect, PEmpty } from '../../components/platform/controls';
import { Panel, DataTable, Tag, StatusBadge, SideDrawer } from '../../components/platform/primitives';
import { ConfirmModal } from '../admin/adminShared';
import { FS, FW, R, inr } from '../../utils/platformTokens';

type Item = typeof import('../../data/platformSeed').SEED.catalogItems[number];
const FILTERS = ['all', 'active', 'draft', 'archived'];

const CatalogEditor: React.FC<{ open: boolean; onClose: () => void; item: Item | null }> = ({ open, onClose, item }) => {
  const { setState } = usePlatform();
  const toast = useToast();
  const isNew = !item;
  const blank: any = { name: '', type: 'Product', price: 0, stock: 0, status: 'draft' };
  const [form, setForm] = useState<any>(blank);
  const [err, setErr] = useState<{ name?: string }>({});
  React.useEffect(() => { if (open) { setForm(item ? { ...item } : blank); setErr({}); } }, [open, item]);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const save = () => {
    const e: typeof err = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    setErr(e); if (Object.keys(e).length) return;
    const clean = { ...form, stock: form.type === 'Service' ? null : +form.stock };
    setState((s) => ({
      ...s,
      catalogItems: isNew ? [{ ...clean, id: (clean.type === 'Service' ? 'SVC-' : 'SKU-') + Math.floor(100 + Math.random() * 900) }, ...s.catalogItems] : s.catalogItems.map((x) => (x.id === item!.id ? { ...clean } : x)),
    }));
    toast.success(isNew ? 'Item added to catalog.' : 'Item updated.'); onClose();
  };
  return (
    <SideDrawer open={open} onClose={onClose} title={isNew ? 'Add catalog item' : `Edit ${item?.name}`}
      footer={<><PButton kind="ghost" onPress={onClose}>Cancel</PButton><PButton kind="accent" onPress={save}>{isNew ? 'Add item' : 'Save changes'}</PButton></>}>
      <View style={{ gap: 14 }}>
        <PTextInput label="Item name" value={form.name} onChangeText={(v) => set('name', v)} error={err.name} />
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}><PSelect label="Type" value={form.type} onChange={(v) => set('type', v)} options={[{ value: 'Product', label: 'Product' }, { value: 'Service', label: 'Service' }]} /></View>
          <View style={{ flex: 1 }}><PTextInput label="Price (₹)" mono keyboardType="numeric" value={String(form.price)} onChangeText={(v) => set('price', +v || 0)} /></View>
        </View>
        {form.type === 'Product' && <PTextInput label="Stock on hand" mono keyboardType="numeric" value={String(form.stock)} onChangeText={(v) => set('stock', v)} />}
        <PSelect label="Status" value={form.status} onChange={(v) => set('status', v)} options={[{ value: 'active', label: 'Active' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' }]} />
      </View>
    </SideDrawer>
  );
};

const CatalogScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState } = usePlatform();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [editor, setEditor] = useState<{ open: boolean; item: Item | null }>({ open: false, item: null });
  const [confirm, setConfirm] = useState<Item | null>(null);

  const items = state.catalogItems.filter((i) => (filter === 'all' || i.status === filter) && `${i.name} ${i.id} ${i.type}`.toLowerCase().includes(q.toLowerCase()));
  const active = state.catalogItems.filter((i) => i.status === 'active').length;
  const lowStock = state.catalogItems.filter((i) => i.stock !== null && i.stock <= 30).length;
  const remove = (i: Item) => { setState((s) => ({ ...s, catalogItems: s.catalogItems.filter((x) => x.id !== i.id) })); toast.success(`${i.name} deleted.`); setConfirm(null); };

  const stockCell = (i: Item) => {
    if (i.stock === null) return <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>—</Text>;
    if (i.stock === 0) return <Text style={{ fontFamily: theme.font.mono, color: c.danger }}>Out</Text>;
    if (i.stock <= 30) return <Text style={{ fontFamily: theme.font.mono, color: c.warn }}>{i.stock}</Text>;
    return <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{i.stock}</Text>;
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <ProductHeader title="Catalog" sub="Products and services you sell."
        action={<PButton kind="accent" icon="plus" onPress={() => setEditor({ open: true, item: null })}>Add item</PButton>} />
      <StatStrip items={[
        { label: 'Catalog items', value: state.catalogItems.length },
        { label: 'Active', value: active, tone: c.success },
        { label: 'Low / out of stock', value: lowStock, tone: lowStock ? c.warn : c.ink },
      ]} />

      <Panel>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <View style={{ flex: 1, minWidth: 200 }}><PTextInput leadingIcon="search" placeholder="Search items…" value={q} onChangeText={setQ} /></View>
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => (
              <Pressable key={f} onPress={() => setFilter(f)}><Tag tone={filter === f ? 'accent' : 'neutral'} size="lg">{f}</Tag></Pressable>
            ))}
          </View>
        </View>
      </Panel>

      <Panel bodyStyle={{ padding: 0 }}>
        <DataTable<Item>
          minWidth={820}
          keyExtractor={(i) => i.id}
          rows={items}
          empty={<PEmpty icon="star" title="No items match">Adjust your search or filters.</PEmpty>}
          columns={[
            {
              key: 'item', header: 'Item', width: 280,
              render: (i) => (
                <View>
                  <Text style={{ fontWeight: FW.medium, color: c.ink }}>{i.name}</Text>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: FS.sm, color: c.ink3 }}>{i.id}</Text>
                </View>
              ),
            },
            { key: 'type', header: 'Type', width: 100, render: (i) => <Tag tone="neutral">{i.type}</Tag> },
            { key: 'price', header: 'Price', width: 100, render: (i) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{inr(i.price)}</Text> },
            { key: 'stock', header: 'Stock', width: 90, render: stockCell },
            { key: 'status', header: 'Status', width: 110, render: (i) => <StatusBadge status={i.status} /> },
            {
              key: 'actions', header: 'Actions', align: 'right',
              render: (i) => (
                <View style={{ flexDirection: 'row', gap: 6, justifyContent: 'flex-end' }}>
                  <PButton size="sm" kind="ghost" icon="edit" iconOnly onPress={() => setEditor({ open: true, item: i })} />
                  <PButton size="sm" kind="ghost" icon="trash" iconOnly onPress={() => setConfirm(i)} />
                </View>
              ),
            },
          ]}
        />
      </Panel>

      <CatalogEditor open={editor.open} item={editor.item} onClose={() => setEditor({ open: false, item: null })} />
      <ConfirmModal open={!!confirm} onClose={() => setConfirm(null)} title={`Delete ${confirm?.name}?`} confirmLabel="Delete item"
        body="This item will be removed from your catalog. This can't be undone." onConfirm={() => confirm && remove(confirm)} />
    </ScrollView>
  );
};

export default CatalogScreen;
